import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { editBlockAttrs } from '../../renderer/editAttrs.js';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { videoSourceTags } from '../../sections/_shared/videoSources.js';
import { VideoPlayButton, videoControlLabels } from '../../sections/_shared/videoControls.js';
import { playButtonField, wantsPlayButton } from '../../sections/_shared/videoPlayButton.js';
import { str } from '../_shared/blockRender.js';
/**
 * `tab-column` — ONE uniform card of the `tabbed-columns` section: a media slot (image OR uploaded clip),
 * a heading, a body and an optional link. It is the Multicolumn card shape (`custom-columns`' `column`
 * block), lifted into the global block registry so it can be NESTED under a `tab` block — which is what
 * makes the section a tabbed multicolumn rather than a second flat grid.
 *
 * ZERO per-card styling on purpose. Every knob — columns per row, gap, image ratio, card alignment, corner
 * radius — lives on the SECTION, exactly as it does in Multicolumn, so a row is uniform by construction and
 * a seller cannot end up with three cards that each look like a different section. This block emits the DOM
 * and nothing else; the rules for `.tabcol-card*` are written once in the section's scoped stylesheet.
 *
 * MEDIA — the card never downloads its clip on its own. Sources ship as `<source data-src>` behind
 * `preload="none"` and the video carries `data-aqx-ct-video`, the marker the shared tab engine
 * (`sections/content-tabs/contentTabsInit`) looks for: it promotes ONLY the visible tab's clips, and only
 * once the section nears the viewport. A five-tab section is therefore one download, not five. The poster
 * falls back to the card's own image so the tile is never an empty box — and that poster is exactly what a
 * visitor with JavaScript disabled keeps looking at.
 */
export const tabColumnBlockSchema = {
    type: 'tab-column',
    name: 'Column',
    fields: [
        { id: 'image', type: 'image', label: 'Image', group: 'Media' },
        {
            id: 'video',
            type: 'video',
            label: 'Video',
            group: 'Media',
            // Uploaded clip, never a pasted URL (project rule) — the control only takes an upload/drag/paste.
            info: 'Shown instead of the image. Use a short MP4/WebM rather than a GIF; the image above is kept as its poster.',
        },
        // Le disque lecture/pause de CETTE carte (20/09/2026, fondatrice : « add this config to all
        // components that has video in them »). Éteint par défaut. La feuille du chrome est écrite une
        // seule fois, dans la feuille scopée de la SECTION — ce bloc n'émet que le DOM, comme le dit
        // l'en-tête, et c'est aussi ce qui évite d'imprimer les mêmes règles autant de fois qu'il y a de
        // cartes.
        playButtonField({
            defaultOn: false,
            group: 'Media',
            visibleWhen: { field: 'video', empty: false },
        }),
        {
            id: 'title',
            type: 'text',
            label: 'Heading',
            group: 'Content',
            // Descriptive placeholder (the page-builder convention): clearly "replace me", readable, professional
            // — NOT lorem-ipsum gibberish and NOT final-looking marketing copy.
            default: 'Column title',
            defaultKey: 'section.column_title',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
            maxLength: 160,
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'Content',
            default: { format: 'html', html: '<p>Each tab has its own image and content.</p>' },
            defaultKey: 'section.tab2_text',
        },
        {
            id: 'link',
            type: 'link',
            label: 'Link',
            group: 'Content',
            info: 'Optional link shown under the text (label + URL).',
        },
    ],
};
/** richtext-ish value → sanitized inner HTML, OBJECT-SAFE (never throws on a legacy bare string). */
function richHtml(v) {
    if (v && typeof v === 'object' && 'html' in v && typeof v.html === 'string') {
        return sanitizeRichtextHtml(v.html);
    }
    return typeof v === 'string' ? sanitizeRichtextHtml(v) : '';
}
const TabColumnBlock = ({ block, context }) => {
    const bs = (block.settings ?? {});
    const video = bs.video;
    const sources = Array.isArray(video?.sources) ? video.sources : [];
    const image = bs.image;
    const title = str(bs.title, '');
    const textHtml = richHtml(bs.text);
    const link = bs.link;
    const linkLabel = str(link?.label, '');
    // `data-aqx-video-state` et non `data-aqx-video` : le second ferait adopter le clip par `videoInit`,
    // et deux moteurs se disputeraient le même fichier — les cartes d'onglets sont promues et mises en
    // pause par `contentTabsInit`, qui garde tous les panneaux dans le DOM. L'attribut ne porte donc que
    // l'état lu par la feuille du chrome, et désigne l'élément que le moteur commande.
    const playOn = wantsPlayButton(bs.showPlayButton, false);
    return (_jsxs("div", { className: "tabcol-card", ...editBlockAttrs(block.id, context), children: [sources.length > 0 ? (_jsxs("div", { className: `tabcol-card__media${playOn ? ' tabcol-card__media--ctl' : ''}`, ...(playOn ? { 'data-aqx-video-state': '' } : null), children: [_jsx("video", { "data-aqx-ct-video": "", loop: true, muted: true, playsInline: true, preload: "none", poster: video?.poster ?? image?.url, children: videoSourceTags(sources) }), playOn ? _jsx(VideoPlayButton, { labels: videoControlLabels(context) }) : null] })) : image?.url ? (_jsx("div", { className: "tabcol-card__media", children: _jsx("img", { src: image.url, alt: image.alt ?? '', loading: "lazy" }) })) : null, _jsxs("div", { className: "tabcol-card__body", children: [title ? _jsx("h3", { className: "tabcol-card__title", children: title }) : null, textHtml ? (_jsx("div", { className: "tabcol-card__text rte", dangerouslySetInnerHTML: { __html: textHtml } })) : null, linkLabel ? (_jsx("a", { className: "tabcol-card__link", href: context.resolveUrl(link), children: linkLabel })) : null] })] }));
};
export const tabColumnBlock = {
    type: 'tab-column',
    schema: tabColumnBlockSchema,
    Component: TabColumnBlock,
    meta: { label: 'Column' },
};
//# sourceMappingURL=index.js.map